import { Injectable } from '@jack-henry/frontend-utils/di';
import { ObservationSource } from '@jack-henry/frontend-utils/observable';
import { LocalStorageService } from '@treasury/utils/services';
import { APP_THEME_STORAGE_KEY, AppTheme } from './theme.service.types';

@Injectable()
export class ThemeService {
    constructor(private storage: LocalStorageService) {
        this.themeAttributeObserver.observe(document.documentElement, { attributes: true });
    }

    private readonly themeAttributeObserver = new MutationObserver(mutations => {
        for (const mutation of mutations) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'theme') {
                const value = (mutation.target as HTMLElement).getAttribute('theme');
                if (!value) {
                    return;
                }
                this.themeSource.emit(value as AppTheme);
            }
        }
    });

    private readonly themeSource = new ObservationSource<AppTheme>(({ emit }) => {
        const defaultTheme = this.getStoredTheme() || AppTheme.Light;
        emit(defaultTheme);
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly theme$ = this.themeSource.toObservable();

    /**
     * Sets the preferred color scheme for the application.
     */
    public init() {
        const preferredColorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches
            ? AppTheme.Dark
            : AppTheme.Light;
        const storedTheme = this.getStoredTheme();

        if (!storedTheme) {
            this.setTheme(preferredColorScheme);
        }

        document.documentElement.setAttribute('theme', storedTheme ?? preferredColorScheme);
    }

    public setTheme(theme: AppTheme) {
        this.storage.set(APP_THEME_STORAGE_KEY, theme);
        document.documentElement.setAttribute('theme', theme);
    }

    private getStoredTheme() {
        return this.storage.get<AppTheme>(APP_THEME_STORAGE_KEY);
    }
}
